/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import {
	Button,
	Drawer,
	Empty,
	Form,
	Input,
	InputNumber,
	message,
	Popconfirm,
	Select,
	Space,
	Spin,
	Switch,
	Table,
	Tabs,
	Tag,
	Typography
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { EditOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import {
	createRecurringTaskTemplate,
	deactivateRecurringTaskTemplate,
	getManagerTasks,
	getRecurringTaskTemplates,
	updateRecurringTaskTemplate
} from './managerTaskApi'
import {
	MANAGER_TASK_EVALUATION_STATUS,
	MANAGER_TASK_FREQUENCY,
	MANAGER_TASK_PRIORITY,
	MANAGER_TASK_WORKFLOW_STATUS,
	ManagerRecurringTaskTemplate,
	ManagerRecurringTaskTemplatePayload,
	ManagerTask,
	ManagerTaskUser
} from './types'
import TemplateFrequencyPicker, { TemplateFrequencyValue, TemplatePattern } from './TemplateFrequencyPicker'
import TaskFilterBar, { TaskFilters, EMPTY_TASK_FILTERS } from './TaskFilterBar'

type AssigneeOption = {
	id: number
	name: string
	role: string
}

interface Props {
	assignees: AssigneeOption[]
	currentUserId?: number
	templates: ManagerRecurringTaskTemplate[]
	onTemplatesChange: (templates: ManagerRecurringTaskTemplate[]) => void
	onOpenEditTask: (task: ManagerTask) => void
	openTemplateId?: number | null
	onOpenTemplateHandled?: () => void
	onSaved?: () => void
}

const PRIORITY_LABELS: Record<MANAGER_TASK_PRIORITY, string> = {
	LOW: 'Bežná',
	MEDIUM: 'Stredná',
	HIGH: 'Vysoká',
	CRITICAL: 'Prioritná'
}

const PRIORITY_COLORS: Record<MANAGER_TASK_PRIORITY, string> = {
	LOW: 'default',
	MEDIUM: 'blue',
	HIGH: 'orange',
	CRITICAL: 'red'
}

const FREQUENCY_LABELS: Record<MANAGER_TASK_FREQUENCY, string> = {
	DAILY: 'Denne',
	WEEKLY: 'Týždenne',
	MONTHLY: 'Mesačne'
}

const nameOf = (user?: ManagerTaskUser | null) => {
	if (!user) return '-'
	return [user.lastName, user.firstName].filter(Boolean).join(' ') || user.email || `#${user.id}`
}

const patternToFrequency = (pattern: TemplatePattern): MANAGER_TASK_FREQUENCY => {
	if (pattern === 'WEEKLY') return MANAGER_TASK_FREQUENCY.WEEKLY
	if (pattern === 'MONTHLY') return MANAGER_TASK_FREQUENCY.MONTHLY
	return MANAGER_TASK_FREQUENCY.DAILY
}

const parseWeekDays = (raw: string | null | undefined): string[] => {
	if (!raw) return []
	if (raw.startsWith('[')) {
		try { return JSON.parse(raw) } catch { return [] }
	}
	return raw.split(',').filter(Boolean)
}

const templateToFreqValue = (t: ManagerRecurringTaskTemplate): TemplateFrequencyValue => {
	let pattern: TemplatePattern
	if (t.frequency === MANAGER_TASK_FREQUENCY.WEEKLY) pattern = 'WEEKLY'
	else if (t.frequency === MANAGER_TASK_FREQUENCY.MONTHLY) pattern = 'MONTHLY'
	else pattern = t.workingDaysOnly ? 'WEEKDAYS' : 'DAILY'
	return {
		pattern,
		weekDays: parseWeekDays(t.weekDays),
		monthDay: t.monthDay ?? null,
		completionWindowHours: t.completionWindowHours ?? null,
		startDate: t.startDate ?? null
	}
}

const toPayload = (
	values: any,
	freq: TemplateFrequencyValue
): ManagerRecurringTaskTemplatePayload => ({
	title: values.title,
	description: values.description || null,
	frequency: patternToFrequency(freq.pattern),
	workingDaysOnly: freq.pattern === 'WEEKDAYS',
	weekDays: freq.pattern === 'WEEKLY' && freq.weekDays.length > 0 ? freq.weekDays.join(',') : null,
	monthDay: freq.pattern === 'MONTHLY' ? (freq.monthDay ?? null) : null,
	timeWindowFrom: null,
	timeWindowTo: null,
	assignedToUserId: values.assignedToUserId,
	slaHours: null,
	penaltyAmount: values.penaltyAmount || null,
	priority: values.priority || MANAGER_TASK_PRIORITY.LOW,
	isActive: values.isActive !== false,
	completionWindowHours: freq.completionWindowHours || null,
	startDate: freq.startDate || null
})

// ---------------------------------------------------------------------------
// TemplateFieldRow — label + content, no remove button
// ---------------------------------------------------------------------------

const TemplateFieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
	<div style={{ marginBottom: 18 }}>
		<div style={{
			fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6
		}}>{label}</div>
		{children}
	</div>
)

// ---------------------------------------------------------------------------
// LinkedTasksSection — sub-component rendered inside the Drawer below the Form
// ---------------------------------------------------------------------------

interface LinkedTasksSectionProps {
	templateId: number
	onOpenTask: (task: ManagerTask) => void
}

const STATUS_LABELS: Record<MANAGER_TASK_WORKFLOW_STATUS, string> = {
	TODO: 'Na spracovanie',
	IN_PROGRESS: 'Rozpracované',
	DONE: 'Hotové',
	VOIDED: 'Zrušené'
}

const STATUS_COLORS: Record<MANAGER_TASK_WORKFLOW_STATUS, string> = {
	TODO: 'default',
	IN_PROGRESS: 'blue',
	DONE: 'green',
	VOIDED: 'red'
}

const EVALUATION_LABELS: Record<MANAGER_TASK_EVALUATION_STATUS, string> = {
	PENDING: 'Čaká',
	SUCCESS: 'Včas',
	FAILED: 'Po termíne'
}

const EVALUATION_COLORS: Record<MANAGER_TASK_EVALUATION_STATUS, string> = {
	PENDING: 'default',
	SUCCESS: 'green',
	FAILED: 'red'
}

const LinkedTasksSection: React.FC<LinkedTasksSectionProps> = ({ templateId, onOpenTask }) => {
	const [filters, setFilters] = useState<TaskFilters>(EMPTY_TASK_FILTERS)

	// When a date range filter is active, use it for the query; otherwise last 28 days
	const queryDateFrom = filters.dateRange
		? filters.dateRange[0].toISOString()
		: moment().subtract(27, 'days').startOf('day').toISOString()
	const queryDateTo = filters.dateRange
		? filters.dateRange[1].toISOString()
		: moment().endOf('day').toISOString()

	const QUERY_LIMIT = 100

	const { data, isLoading } = useQuery({
		queryKey: ['managerTasks', { recurringTemplateId: templateId, dateFrom: queryDateFrom, dateTo: queryDateTo }],
		queryFn: () => getManagerTasks({
			recurringTemplateId: templateId,
			dateFrom: queryDateFrom,
			dateTo: queryDateTo,
			limit: QUERY_LIMIT
		}),
		keepPreviousData: true
	})

	const allTasks: ManagerTask[] = data?.data ?? []
	const totalCount: number = data?.meta?.total ?? 0
	const isTruncated = totalCount > QUERY_LIMIT

	// Derive unique assignees for the Zodpovedný filter
	const uniqueAssignees: ManagerTaskUser[] = []
	const seenIds = new Set<number>()
	allTasks.forEach((t) => {
		if (t.assignedTo && !seenIds.has(t.assignedTo.id)) {
			seenIds.add(t.assignedTo.id)
			uniqueAssignees.push(t.assignedTo)
		}
	})

	// Apply client-side filters
	const filteredTasks = allTasks.filter((t) => {
		if (filters.statuses.length > 0 && !filters.statuses.includes(t.workflowStatus)) return false
		if (filters.evaluations.length > 0 && !filters.evaluations.includes(t.evaluationStatus)) return false
		if (filters.assigneeId !== null && t.assignedToUserId !== filters.assigneeId) return false
		return true
	})

	const columns: ColumnsType<ManagerTask> = [
		{
			title: 'Termín',
			dataIndex: 'deadlineAt',
			width: 140,
			render: (v) => (v ? moment(v).format('DD.MM.YYYY HH:mm') : '—')
		},
		{
			title: 'Stav',
			dataIndex: 'workflowStatus',
			width: 130,
			render: (v: MANAGER_TASK_WORKFLOW_STATUS) => <Tag color={STATUS_COLORS[v]}>{STATUS_LABELS[v]}</Tag>
		},
		{
			title: 'Výsledok',
			dataIndex: 'evaluationStatus',
			width: 130,
			render: (v: MANAGER_TASK_EVALUATION_STATUS) => <Tag color={EVALUATION_COLORS[v]}>{EVALUATION_LABELS[v]}</Tag>
		},
		{
			title: 'Zodpovedný',
			render: (_v, t) => nameOf(t.assignedTo)
		}
	]

	return (
		<div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
			<Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
				Prepojené úlohy
			</Typography.Text>
			<div style={{ marginBottom: 12 }}>
				<TaskFilterBar
					filters={filters}
					onChange={setFilters}
					assignees={uniqueAssignees}
				/>
			</div>
			{isTruncated && (
				<div style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					padding: '7px 12px',
					marginBottom: 10,
					background: '#fffbeb',
					border: '1px solid #fcd34d',
					borderRadius: 6,
					fontSize: 12.5,
					color: '#92400e'
				}}>
					<span>⚠</span>
					<span>
						Zobrazených <strong>{QUERY_LIMIT}</strong> z <strong>{totalCount}</strong> úloh.
						{' '}Spresni filtre dátumového rozsahu pre zobrazenie všetkých záznamov.
					</span>
				</div>
			)}
			<Spin spinning={isLoading}>
				<Table<ManagerTask>
					size="small"
					columns={columns}
					dataSource={filteredTasks}
					rowKey="id"
					pagination={false}
					onRow={(task) => ({ onClick: () => onOpenTask(task), style: { cursor: 'pointer' } })}
					locale={{ emptyText: <Empty description="Žiadne prepojené úlohy v tomto období" /> }}
				/>
			</Spin>
		</div>
	)
}

const DEFAULT_FREQ_VALUE: TemplateFrequencyValue = {
	pattern: 'DAILY',
	weekDays: [],
	monthDay: null,
	completionWindowHours: null,
	startDate: null
}

const RecurringTemplateManager = ({
	assignees,
	currentUserId,
	onTemplatesChange,
	onOpenEditTask,
	openTemplateId,
	onOpenTemplateHandled,
	onSaved
}: Props) => {
	const [form] = Form.useForm()
	const [isDrawerOpen, setIsDrawerOpen] = useState(false)
	const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null)
	const [isSaving, setIsSaving] = useState(false)
	const [freqValue, setFreqValue] = useState<TemplateFrequencyValue>(DEFAULT_FREQ_VALUE)
	const [activeTab, setActiveTab] = useState<string>('settings')

	const queryClient = useQueryClient()

	const {
		data: allTemplates = [],
		isLoading: isLoadingTemplates,
		isError: isTemplatesError
	} = useQuery({
		queryKey: ['managerRecurringTaskTemplates', 'all'],
		queryFn: () => getRecurringTaskTemplates(),
		keepPreviousData: true,
		retry: 1
	})

	const openCreate = () => {
		setEditingTemplateId(null)
		form.resetFields()
		form.setFieldsValue({
			priority: MANAGER_TASK_PRIORITY.LOW,
			isActive: true,
			assignedToUserId: currentUserId
		})
		setFreqValue(DEFAULT_FREQ_VALUE)
		setActiveTab('settings')
		setIsDrawerOpen(true)
	}

	const openEdit = (template: ManagerRecurringTaskTemplate) => {
		setEditingTemplateId(template.id)
		form.resetFields()
		form.setFieldsValue({
			title: template.title,
			description: template.description,
			assignedToUserId: template.assignedToUserId,
			penaltyAmount: template.penaltyAmount,
			priority: template.priority,
			isActive: template.isActive
		})
		setFreqValue(templateToFreqValue(template))
		setActiveTab('settings')
		setIsDrawerOpen(true)
	}

	useEffect(() => {
		if (!openTemplateId || !allTemplates.length) return
		const target = allTemplates.find((t) => t.id === openTemplateId)
		if (target) {
			openEdit(target)
			onOpenTemplateHandled?.()
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openTemplateId, allTemplates])

	const handleSave = async () => {
		try {
			const values = await form.validateFields()
			const payload = toPayload(values, freqValue)
			setIsSaving(true)
			if (editingTemplateId) {
				await updateRecurringTaskTemplate(editingTemplateId, payload)
				message.success('Šablóna bola upravená')
			} else {
				await createRecurringTaskTemplate(payload)
				message.success('Šablóna bola vytvorená')
			}
			setIsDrawerOpen(false)
			setEditingTemplateId(null)
			queryClient.invalidateQueries(['managerRecurringTaskTemplates'])
			const activeOnly = await getRecurringTaskTemplates({ isActive: true })
			onTemplatesChange(activeOnly)
			onSaved?.()
		} catch (err: any) {
			if (err?.errorFields) {
				return
			}
			message.error('Chyba pri ukladaní šablóny. Skúste to znova.')
		} finally {
			setIsSaving(false)
		}
	}

	const templateColumns: ColumnsType<ManagerRecurringTaskTemplate> = [
		{
			title: 'Názov',
			dataIndex: 'title',
			render: (_v, t) => (
				<Typography.Text
					strong
					type={t.isActive ? undefined : 'secondary'}
					style={t.isActive ? undefined : { textDecoration: 'line-through' }}
				>
					{t.title}
				</Typography.Text>
			)
		},
		{
			title: 'Frekvencia',
			dataIndex: 'frequency',
			width: 130,
			render: (v: MANAGER_TASK_FREQUENCY) => {
				const colorMap: Record<MANAGER_TASK_FREQUENCY, string> = { DAILY: 'blue', WEEKLY: 'purple', MONTHLY: 'cyan' }
				return <Tag color={colorMap[v]}>{FREQUENCY_LABELS[v]}</Tag>
			}
		},
		{
			title: 'Priorita',
			dataIndex: 'priority',
			width: 120,
			render: (v: MANAGER_TASK_PRIORITY) => <Tag color={PRIORITY_COLORS[v]}>{PRIORITY_LABELS[v]}</Tag>
		},
		{
			title: 'Zodpovedný',
			width: 180,
			render: (_v, t) => nameOf(t.assignedTo)
		},
		{
			title: 'Aktívna',
			width: 110,
			render: (_v, t) => <Tag color={t.isActive ? 'green' : 'default'}>{t.isActive ? 'Aktívna' : 'Neaktívna'}</Tag>
		},
		{
			title: 'Akcie',
			width: 160,
			render: (_v, t) => (
				<Button size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEdit(t) }}>
					Upraviť šablónu
				</Button>
			)
		}
	]

	return (
		<Space direction={'vertical'} style={{ width: '100%' }} size={16}>
			<div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>+ Nová šablóna</Button>
			</div>
			<Table
				columns={templateColumns}
				dataSource={allTemplates}
				rowKey="id"
				loading={isLoadingTemplates}
				pagination={{ pageSize: 20 }}
				onRow={(template) => ({ onClick: () => openEdit(template) })}
				locale={{
					emptyText: isTemplatesError
						? <Empty description="Chyba pri načítaní šablón. Skúste obnoviť stránku." image={Empty.PRESENTED_IMAGE_SIMPLE} />
						: <Empty description="Žiadne plánované úlohy" />
				}}
			/>
			<Drawer
				width={600}
				placement="right"
				visible={isDrawerOpen}
				destroyOnClose
				title={
					<span>
						<SyncOutlined style={{ color: '#1890ff', marginRight: 8 }} />
						{editingTemplateId
							? (allTemplates.find((t) => t.id === editingTemplateId)?.title ?? 'Šablóna')
							: 'Nová šablóna'}
					</span>
				}
				onClose={() => setIsDrawerOpen(false)}
				footer={activeTab === 'settings' ? (
					<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
						<Button onClick={() => setIsDrawerOpen(false)}>Zrušiť</Button>
						<Button type="primary" loading={isSaving} onClick={handleSave}>Uložiť zmeny</Button>
						{editingTemplateId && (
							<Popconfirm
								title="Deaktivovať šablónu?"
								description="Šablóna sa nebude ďalej používať na generovanie nových úloh."
								okText="Deaktivovať"
								cancelText="Zrušiť"
								okButtonProps={{ danger: true }}
								onConfirm={async () => {
									await deactivateRecurringTaskTemplate(editingTemplateId)
									message.success('Šablóna bola deaktivovaná')
									setIsDrawerOpen(false)
									queryClient.invalidateQueries(['managerRecurringTaskTemplates'])
									const activeOnly = await getRecurringTaskTemplates({ isActive: true })
									onTemplatesChange(activeOnly)
								}}
							>
								<Button danger>Deaktivovať</Button>
							</Popconfirm>
						)}
					</div>
				) : null}
			>
				<Tabs activeKey={activeTab} onChange={setActiveTab}>
					<Tabs.TabPane tab="Nastavenia" key="settings">
						<Form form={form} layout="vertical">
							{/* Zadávateľ – static, only when editing */}
							{editingTemplateId && (() => {
								const t = allTemplates.find((tmpl) => tmpl.id === editingTemplateId)
								const creator = t?.createdBy
								return creator ? (
									<div style={{
										marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
									}}>
										<span style={{ fontSize: 12, color: '#9ca3af' }}>Zadávateľ:</span>
										<span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{nameOf(creator)}</span>
									</div>
								) : null
							})()}

							{/* Title */}
							<Form.Item
								name="title"
								rules={[{ required: true, message: 'Zadaj názov' }]}
								style={{ marginBottom: 24 }}
							>
								<Input
									placeholder="Názov šablóny..."
									bordered={false}
									style={{
										fontSize: 22,
										fontWeight: 600,
										color: '#111827',
										borderBottom: '2px solid #e5e7eb',
										borderRadius: 0,
										padding: '0 0 8px',
										boxShadow: 'none'
									}}
								/>
							</Form.Item>

							{/* Opakovanie */}
							<TemplateFieldRow label="Opakovanie">
								<TemplateFrequencyPicker value={freqValue} onChange={setFreqValue} />
							</TemplateFieldRow>

							{/* Čas na splnenie */}
							<TemplateFieldRow label="Čas na splnenie">
								<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
									<InputNumber
										min={0}
										value={freqValue.completionWindowHours ?? undefined}
										onChange={(val) => setFreqValue((prev) => ({ ...prev, completionWindowHours: val ?? null }))}
										placeholder="0"
										style={{ width: 100 }}
									/>
									<span style={{ fontSize: 13, color: '#6b7280' }}>hodín</span>
								</div>
							</TemplateFieldRow>

							{/* Zodpovedný */}
							<TemplateFieldRow label="Zodpovedný">
								<Form.Item name="assignedToUserId" noStyle rules={[{ required: true, message: 'Vyber zodpovedného' }]}>
									<Select showSearch optionFilterProp="children" style={{ width: '100%' }}>
										{assignees.map((a) => (
											<Select.Option key={a.id} value={a.id}>{a.name}</Select.Option>
										))}
									</Select>
								</Form.Item>
							</TemplateFieldRow>

							{/* Popis */}
							<TemplateFieldRow label="Popis">
								<Form.Item name="description" noStyle>
									<Input.TextArea
										rows={3}
										placeholder="Popis šablóny..."
										style={{ resize: 'none' }}
									/>
								</Form.Item>
							</TemplateFieldRow>

							{/* Priorita */}
							<TemplateFieldRow label="Priorita">
								<Form.Item name="priority" noStyle>
									<Select style={{ width: '100%' }}>
										{Object.values(MANAGER_TASK_PRIORITY).filter((p) => p !== 'MEDIUM').map((p) => (
											<Select.Option key={p} value={p}>{PRIORITY_LABELS[p]}</Select.Option>
										))}
									</Select>
								</Form.Item>
							</TemplateFieldRow>

							{/* Penále */}
							<TemplateFieldRow label="Penále (€)">
								<Form.Item name="penaltyAmount" noStyle>
									<InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
								</Form.Item>
							</TemplateFieldRow>

							{/* Aktívna switch */}
							<div style={{
								display: 'flex',
								alignItems: 'center',
								gap: 10,
								paddingTop: 8,
								borderTop: '1px solid #f0f0f0'
							}}>
								<Form.Item name="isActive" valuePropName="checked" noStyle>
									<Switch />
								</Form.Item>
								<span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Aktívna šablóna</span>
							</div>
						</Form>
					</Tabs.TabPane>
					<Tabs.TabPane tab="Prepojené úlohy" key="linked" disabled={!editingTemplateId}>
						{editingTemplateId && (
							<LinkedTasksSection
								templateId={editingTemplateId}
								onOpenTask={onOpenEditTask}
							/>
						)}
					</Tabs.TabPane>
				</Tabs>
			</Drawer>
		</Space>
	)
}

export default RecurringTemplateManager
